import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CONSTANTS from "../utils/constants";
import { useTranslation } from "react-i18next";
import Select from "react-select";

export default function AdminBannerAdd() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    order_number: 0,
    image: null,
    category: "home",
    isActive: true,
    link_type: "none",
    link_id: "",
    link_subcategory_id: "",
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState({
    categories: false,
    products: false,
  });

  // Ana kategorileri filtrele (parent_id'si NULL olanlar)
  const mainCategories = categories.filter((cat) => cat.parent_id === null);

  // Alt kategorileri filtrele (parent_id'si olanlar)
  const allSubcategories = categories.filter((cat) => cat.parent_id !== null);

  // React Select için option formatına çevirme
  const mainCategoryOptions = mainCategories.map((cat) => ({
    value: cat.id,
    label: cat.name_tm || cat.name_ru || cat.name_en || `Kategori ${cat.id}`,
  }));

  const subcategoryOptions = allSubcategories.map((subcat) => ({
    value: subcat.id,
    label:
      subcat.name_tm ||
      subcat.name_ru ||
      subcat.name_en ||
      `Alt Kategori ${subcat.id}`,
  }));

  const productOptions = products.map((product) => ({
    value: product.id,
    label: `${product.name_tm || product.name_ru || product.name_en || 'Ürün'} | (${product.barcode || 'Barkod Yok'})`,
    category: product.category_id,
    subcategory: product.subcategory_id,
  }));

  const selectedMainCategory = mainCategoryOptions.find(
    (opt) => opt.value === parseInt(formData.link_id),
  );

  const selectedSubcategory = subcategoryOptions.find(
    (opt) => opt.value === parseInt(formData.link_subcategory_id),
  );

  const selectedProduct = productOptions.find(
    (opt) => opt.value === parseInt(formData.link_id),
  );

  // Kategorileri getir
  const fetchCategories = async () => {
    setLoading((prev) => ({ ...prev, categories: true }));
    try {
      const res = await axios.get(`${CONSTANTS.baseRoute}/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading((prev) => ({ ...prev, categories: false }));
    }
  };

  // Ürünleri getir
  const fetchProducts = async () => {
    setLoading((prev) => ({ ...prev, products: true }));
    try {
      const res = await axios.get(`${CONSTANTS.baseRoute}/products/admin`);
      setProducts(res.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading((prev) => ({ ...prev, products: false }));
    }
  };

  // Link tipi değiştiğinde ilgili verileri getir
  useEffect(() => {
    if (formData.link_type === "category" || formData.link_type === "subcategory") {
      fetchCategories();
      setFormData((prev) => ({
        ...prev,
        link_id: "",
        link_subcategory_id: "",
      }));
    } else if (formData.link_type === "product") {
      fetchProducts();
      setFormData((prev) => ({
        ...prev,
        link_id: "",
        link_subcategory_id: "",
      }));
    } else {
      setSubcategories([]);
    }
  }, [formData.link_type]);

  // Ana kategori seçildiğinde alt kategorileri güncelle
  useEffect(() => {
    if (formData.link_type === "subcategory" && formData.link_id) {
      const subs = categories.filter(
        (cat) => cat.parent_id === parseInt(formData.link_id),
      );
      setSubcategories(subs);
      setFormData((prev) => ({ ...prev, link_subcategory_id: "" }));
    } else if (formData.link_type !== "subcategory") {
      setSubcategories([]);
    }
  }, [formData.link_id, formData.link_type, categories]);

  // Kategori veya alt kategori seçildiğinde ürünleri filtrele
  useEffect(() => {
    if (formData.link_type === "product" && products.length > 0) {
      if (formData.link_subcategory_id) {
        const filtered = products.filter(
          (product) => product.subcategory_id === parseInt(formData.link_subcategory_id),
        );
        setFilteredProducts(filtered);
      } else if (formData.link_id) {
        const filtered = products.filter(
          (product) => product.category_id === parseInt(formData.link_id),
        );
        setFilteredProducts(filtered);
      } else {
        setFilteredProducts(products);
      }
    }
  }, [
    formData.link_id,
    formData.link_subcategory_id,
    products,
    formData.link_type,
  ]);

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;

    if (id === "link_type") {
      setFormData((prev) => ({
        ...prev,
        [id]: value,
        link_id: "",
        link_subcategory_id: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [id]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleMainCategoryChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      link_id: selected ? selected.value.toString() : "",
      link_subcategory_id: "",
    }));
  };

  const handleSubcategoryChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      link_subcategory_id: selected ? selected.value.toString() : "",
    }));
  };

  const handleProductChange = (selected) => {
    setFormData((prev) => ({
      ...prev,
      link_id: selected ? selected.value.toString() : "",
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setMessage({
          type: "danger",
          text: t("adminBannerAdd.onlyImagesAllowed"),
        });
        return;
      }
      setFormData((prev) => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
      setMessage({ type: "", text: "" });
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setMessage({
        type: "danger",
        text: t("adminBannerAdd.nameRequired"),
      });
      return false;
    }

    if (!formData.image) {
      setMessage({
        type: "danger",
        text: t("adminBannerAdd.imageRequired"),
      });
      return false;
    }

    // Link tipi seçilmişse ID kontrolü yap
    if (formData.link_type !== "none") {
      if (formData.link_type === "subcategory") {
        if (!formData.link_id || !formData.link_subcategory_id) {
          setMessage({
            type: "danger",
            text:
              t("adminBannerAdd.linkRequired") ||
              "Lütfen bir ana kategori ve alt kategori seçin",
          });
          return false;
        }
      } else if (!formData.link_id) {
        setMessage({
          type: "danger",
          text:
            t("adminBannerAdd.linkRequired") ||
            "Lütfen bir kategori veya ürün seçin",
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setMessage({ type: "", text: "" });

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name.trim());
      formDataToSend.append("order_number", formData.order_number);
      formDataToSend.append("image", formData.image);
      formDataToSend.append("category", formData.category);
      formDataToSend.append("is_active", formData.isActive);
      formDataToSend.append("link_type", formData.link_type);

      if (formData.link_id) {
        formDataToSend.append("link_id", formData.link_id);
      }

      if (formData.link_subcategory_id) {
        formDataToSend.append("link_subcategory_id", formData.link_subcategory_id);
      }

      await axios.post(`${CONSTANTS.baseRoute}/banners`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMessage({
        type: "success",
        text: t("adminBannerAdd.successMessage"),
      });

      setTimeout(() => {
        navigate("/admin/home/banner");
      }, 1500);
    } catch (error) {
      console.error("Error adding banner:", error);
      setMessage({
        type: "danger",
        text:
          error.response?.data?.message ||
          t("adminBannerAdd.submitError"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // React Select stilleri
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "58px",
      borderRadius: "0.375rem",
      borderColor: state.isFocused ? "#F47D39" : "#cccc",
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(244, 125, 57, 0.25)"
        : null,
      "&:hover": {
        borderColor: "#F47D39",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#F47D39"
        : state.isFocused
          ? "#e9ecef"
          : null,
      color: state.isSelected ? "white" : "black",
    }),
  };

  return (
    <div className="admin-main p-2">
      <div className="d-flex gap-2 align-items-center mb-3">
        <button className="btn btn-light" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left"></i>
        </button>
        <button className="btn btn-light" onClick={() => navigate(+1)}>
          <i className="bi bi-chevron-right"></i>
        </button>
        <h5 className="fw-bold mb-0">{t("adminBannerAdd.title")}</h5>
      </div>

      {message.text && (
        <div
          className={`alert alert-${message.type} shadow-sm mb-4`}
          role="alert"
        >
          {message.text}
        </div>
      )}

      <div className="card shadow-sm border-0 rounded">
        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              {/* Banner Adı */}
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control rounded-3"
                    id="name"
                    placeholder={t("adminBannerAdd.namePlaceholder")}
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                  <label htmlFor="name">
                    {t("adminBannerAdd.name")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                </div>
              </div>

              {/* Sıra Numarası */}
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="number"
                    className="form-control rounded-3"
                    id="order_number"
                    placeholder={t("adminBannerAdd.orderPlaceholder")}
                    value={formData.order_number}
                    onChange={handleInputChange}
                    min="0"
                  />
                  <label htmlFor="order_number">
                    {t("adminBannerAdd.order")}
                  </label>
                </div>
              </div>

              {/* Link Tipi */}
              <div className="col-md-6">
                <div className="form-floating">
                  <select
                    className="form-select rounded-3"
                    id="link_type"
                    value={formData.link_type}
                    onChange={handleInputChange}
                  >
                    <option value="none">
                      {t("adminBannerAdd.noLink") || "Bağlantı Yok"}
                    </option>
                    <option value="category">
                      {t("adminBannerAdd.linkToCategory") || "Kategoriye Bağla"}
                    </option>
                    <option value="subcategory">
                      {t("adminBannerAdd.linkToSubcategory") || "Alt Kategoriye Bağla"}
                    </option>
                    <option value="product">
                      {t("adminBannerAdd.linkToProduct") || "Ürüne Bağla"}
                    </option>
                  </select>
                  <label htmlFor="link_type">
                    {t("adminBannerAdd.linkType") || "Bağlantı Tipi"}
                  </label>
                </div>
              </div>

              {/* Ana Kategori Seçimi */}
              {(formData.link_type === "category" ||
                formData.link_type === "subcategory") && (
                <div className="col-6">
                  <Select
                    options={mainCategoryOptions}
                    value={selectedMainCategory || null}
                    onChange={handleMainCategoryChange}
                    placeholder={
                      loading.categories
                        ? "Yükleniyor..."
                        : t("adminBannerAdd.selectCategory") || "Kategori Seçin"
                    }
                    isClearable
                    isSearchable
                    styles={selectStyles}
                    isLoading={loading.categories}
                    noOptionsMessage={() => "Kategori bulunamadı"}
                  />
                </div>
              )}

              {/* Alt Kategori Seçimi */}
              {formData.link_type === "subcategory" && formData.link_id && (
                <div className="col-12">
                  <Select
                    options={subcategoryOptions}
                    value={selectedSubcategory || null}
                    onChange={handleSubcategoryChange}
                    placeholder={
                      subcategories.length === 0
                        ? "Bu kategoride alt kategori yok"
                        : t("adminBannerAdd.selectSubcategory") || "Alt Kategori Seçin"
                    }
                    isClearable
                    isSearchable
                    styles={selectStyles}
                    noOptionsMessage={() => "Alt kategori bulunamadı"}
                    isDisabled={subcategories.length === 0}
                  />
                  {subcategories.length === 0 && (
                    <small className="text-muted">
                      Bu ana kategoride alt kategori bulunmamaktadır.
                    </small>
                  )}
                </div>
              )}

              {/* Ürün Seçimi */}
              {formData.link_type === "product" && (
                <div className="col-6">
                  <Select
                    options={productOptions}
                    value={selectedProduct || null}
                    onChange={handleProductChange}
                    placeholder={
                      loading.products
                        ? "Yükleniyor..."
                        : t("adminBannerAdd.selectProduct") || "Ürün Seçin"
                    }
                    isClearable
                    isSearchable
                    styles={selectStyles}
                    isLoading={loading.products}
                    noOptionsMessage={() => "Ürün bulunamadı"}
                  />
                </div>
              )}

              {/* Resim Yükleme */}
              <div className="col-12">
                <label htmlFor="image" className="form-label fw-semibold small">
                  {t("adminBannerAdd.image")}{" "}
                  <span className="text-danger">*</span>
                  <small className="text-muted ms-2">
                    ({t("adminBannerAdd.recommendedSize") || "847×423"})
                  </small>
                </label>

                <input
                  type="file"
                  className="form-control rounded-3"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  required={!imagePreview}
                />

                {imagePreview && (
                  <div className="mt-3 overflow-hidden">
                    <img
                      src={imagePreview}
                      alt={t("adminBannerAdd.preview")}
                      className="img-fluid w-100"
                      style={{ maxHeight: "280px", objectFit: "contain" }}
                    />
                  </div>
                )}
              </div>

              {/* Aktif Durumu */}
              <div className="col-md-6 d-flex align-items-center">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="isActive">
                    {t("adminBannerAdd.active")}
                  </label>
                </div>
              </div>

              {/* Gönder Butonu */}
              <div className="col-12 d-flex justify-content-end mt-4">
                <button
                  type="submit"
                  className="btn btn-primary px-5 py-2 fw-bold rounded-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {t("adminBannerAdd.submitting")}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-circle me-2"></i>
                      {t("adminBannerAdd.submit")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}